"use client"

import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"

const TravelBookings = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [registration, setRegistration] = useState(null)
  const [receiptDownloaded, setReceiptDownloaded] = useState(false)
  const [paymentVerified, setPaymentVerified] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  
  // Function to download receipt
  const downloadReceipt = async (receiptUrl) => {
    if (!receiptUrl || receiptDownloaded) return;
    
    try {
      console.log('Attempting to download receipt from:', receiptUrl);
      
      // First, try to open in a new tab (most reliable method)
      try {
        const newWindow = window.open(receiptUrl, '_blank');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          throw new Error('Popup was blocked');
        }
        console.log('Opened receipt in new tab');
        setReceiptDownloaded(true);
        return;
      } catch (tabError) {
        console.log('Could not open in new tab, trying direct download:', tabError);
      }
      
      // If new tab was blocked, try direct download
      try {
        // Create a temporary anchor element
        const link = document.createElement('a');
        link.href = receiptUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = `receipt-${new Date().toISOString().split('T')[0]}.pdf`;
        
        // Append to body, click and clean up
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
        
        console.log('Direct download initiated');
        setReceiptDownloaded(true);
        return;
      } catch (directError) {
        console.log('Direct download failed, trying fetch:', directError);
      }
      
      // If direct download fails, try fetch and createObjectURL
      try {
        const response = await fetch(receiptUrl, {
          method: 'GET',
          credentials: 'include', // Include cookies if needed
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch receipt: ${response.status} ${response.statusText}`);
        }

        // Create a blob from the response
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create a temporary anchor element
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `receipt-${new Date().toISOString().split('T')[0]}.pdf`;
        
        // Append to body, click and clean up
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
        
        console.log('Blob download initiated');
        setReceiptDownloaded(true);
      } catch (fetchError) {
        console.error('All download methods failed:', fetchError);
        // As a last resort, show a message to the user
        alert('Could not automatically download receipt. Please click the download button to try again.');
      }
    } catch (error) {
      console.error('Unexpected error in downloadReceipt:', error);
      alert('An error occurred while trying to download the receipt. Please try again or contact support.');
    }
  };

  // Parse query parameters from URL
  const getQueryParams = () => {
    try {
      // Get the full URL
      const fullUrl = window.location.href;
      
      // Extract the hash part and parse it as URL
      const hashUrl = new URL(fullUrl);
      const hashParams = new URLSearchParams(hashUrl.hash.replace(/^#/, '').split('?')[1] || '');
      
      const params = {};

      // Get params from hash (for hash-based routing)
      for (const [key, value] of hashParams.entries()) {
        params[key] = value;
      }

      // Also check for common variations of parameter names
      if (!params.registration_id && params.registrationId) {
        params.registration_id = params.registrationId;
      }
      if (!params.tx_ref && params.txRef) {
        params.tx_ref = params.txRef;
      }

      console.log("TravelBookings - Parsed parameters:", params);
      return params;
    } catch (error) {
      console.error("Error parsing URL parameters:", error);
      return {};
    }
  }

  const [params, setParams] = useState(getQueryParams())

  // Update params when location changes
  useEffect(() => {
    console.log("TravelBookings - Location changed:", location)
    setParams(getQueryParams())
  }, [location])

  const { tx_ref: txRef, registration_id: registrationId, status } = params

  // Log the URL parameters for debugging
  useEffect(() => {
    console.log("TravelBookings - URL Parameters:", {
      txRef,
      registrationId,
      status,
      fullUrl: window.location.href,
      search: window.location.search,
      hash: window.location.hash,
      location: location.pathname + location.search,
    })

    // If no parameters found, redirect to home
    if (!txRef && !registrationId) {
      console.warn("No transaction reference or registration ID found, redirecting to home")
      // Store the current URL in sessionStorage before redirecting
      sessionStorage.setItem('paymentRedirect', window.location.href)
      navigate("/")
      return
    }
  }, [txRef, registrationId, status, location, navigate])

  useEffect(() => {
    // Verify payment status when component mounts
    const verifyPayment = async () => {
      try {
        if (!registrationId && !txRef) {
          // Try to get the parameters from sessionStorage as fallback
          const storedParams = sessionStorage.getItem('paymentParams');
          if (storedParams) {
            const parsedParams = JSON.parse(storedParams);
            if (parsedParams.registrationId || parsedParams.txRef) {
              console.log("Using parameters from sessionStorage:", parsedParams);
              setParams(prev => ({
                ...prev,
                registration_id: parsedParams.registrationId || prev.registration_id,
                tx_ref: parsedParams.txRef || prev.tx_ref
              }));
              return; // Will trigger a re-render with the new params
            }
          }
          throw new Error("Missing required parameters. Please ensure you were redirected here after payment.")
        }

        console.log("Verifying payment with:", { registrationId, txRef, status });
        
        // Store the parameters in sessionStorage as fallback
        sessionStorage.setItem('paymentParams', JSON.stringify({
          registrationId,
          txRef,
          timestamp: Date.now()
        }));

        // Make API call to verify payment
        const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:5002";
        const verifyUrl = new URL(`${apiUrl}/api/payment/verify`);

        // Add parameters to the verification URL
        if (txRef) {
          verifyUrl.searchParams.append("tx_ref", txRef);
        }
        if (registrationId) {
          verifyUrl.searchParams.append("registration_id", registrationId);
        }
        
        // Add cache-busting parameter
        verifyUrl.searchParams.append("_", Date.now().toString());

        console.log("Verification URL:", verifyUrl.toString())

        const verifyResponse = await fetch(verifyUrl.toString(), {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        })

        if (!verifyResponse.ok) {
          const errorData = await verifyResponse.json().catch(() => ({}))
          throw new Error(errorData.message || "Payment verification failed")
        }

        const result = await verifyResponse.json()
        console.log("Payment verification result:", result)

        if (!result.success) {
          throw new Error(result.message || "Payment not completed or verification failed")
        }
        
        // Store the registration details
        if (result.data) {
          const reg = result.data.registration || result.data;
          const receiptUrl = result.data.receipt_url || (result.data.data && result.data.data.receipt_url);
          
          // If minimal fields, fetch full record
          if (!reg?.firstName && registrationId) {
            try {
              const regRes = await fetch(`${apiUrl}/api/register/${registrationId}`);
              if (regRes.ok) {
                const fullReg = await regRes.json();
                setRegistration({
                  ...(fullReg.data || fullReg),
                  receiptUrl: receiptUrl || (fullReg.data?.receiptUrl || fullReg.receiptUrl),
                  hotelDiscountCode: result.data.hotelDiscountCode || (fullReg.data?.hotelDiscountCode || fullReg.hotelDiscountCode)
                });
              } else {
                setRegistration({
                  ...reg,
                  receiptUrl: receiptUrl || reg.receiptUrl,
                  hotelDiscountCode: result.data.hotelDiscountCode || reg.hotelDiscountCode
                });
              }
            } catch (err) {
              console.error('Error fetching full registration:', err);
              setRegistration({
                ...reg,
                receiptUrl: receiptUrl || reg?.receiptUrl,
                hotelDiscountCode: result.data.hotelDiscountCode || reg.hotelDiscountCode
              });
            }
          } else {
            setRegistration({
              ...reg,
              receiptUrl: receiptUrl || reg?.receiptUrl,
              hotelDiscountCode: result.data.hotelDiscountCode || reg.hotelDiscountCode
            });
          }
        } else {
          // If no registration in response, create a basic one from URL params
          setRegistration({
            _id: registrationId,
            txRef: txRef,
            status: status || 'completed',
            hotelDiscountCode: result.data?.hotelDiscountCode
          })
        }
        
        setLoading(false)
      } catch (err) {
        console.error("Error verifying payment:", err)
        setError(err.message || "Failed to verify payment. Please contact support with your transaction reference.")
        setLoading(false)
      }
    }

    // Only verify if we have the required parameters
    if (txRef || registrationId) {
      verifyPayment()
    } else {
      setLoading(false)
      setError("Missing payment information. Please try registering again.")
    }
  }, [registrationId, txRef, status])

  // --- Removed automatic receipt download ---
  // Users can now choose to download the receipt manually via the button.
  // If you’d like to re-enable auto-download in future, wrap the call in
  // a setTimeout and adjust the delay as needed.

  
  // Handle payment verification success
  useEffect(() => {
    if (registration && (registration.paymentStatus === 'completed' || registration.status === 'completed') && !paymentVerified) {
      console.log('Payment verified, setting paymentVerified to true');
      setPaymentVerified(true);
    }
  }, [registration, paymentVerified]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying your payment...</p>
          {registrationId && (
            <p className="mt-2 text-sm text-gray-500">
              Registration ID: {registrationId}
            </p>
          )}
          {txRef && (
            <p className="text-sm text-gray-500">
              Transaction Reference: {txRef}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Verification Issue</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <a
              href="/register"
              className="block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Return to Registration
            </a>
            <button
              onClick={() => window.location.reload()}
              className="block w-full bg-gray-200 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-300 transition-colors"
            >
              Retry Verification
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Display registration details
  // Explicit handler so users can download when they’re ready
  const handleDownloadReceipt = () => {
    if (registration?.receiptUrl) {
      downloadReceipt(registration.receiptUrl);
    }
  };

  const renderRegistrationDetails = () => {
    if (!registration) return null;
    
    // Add download receipt button at the top
    if (registration.receiptUrl) {
      return (
        <div className="mb-6 bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-blue-800 mb-2">Payment Successful!</h3>
          <p className="text-blue-700 mb-3">Your payment has been processed successfully.</p>
          <button
            onClick={handleDownloadReceipt}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Download Receipt
          </button>
          <p className="text-sm text-blue-600 mt-2">
            Please download and save your receipt for your records.
          </p>
        </div>
      );
    }

    // Determine available hotel discount code
    const discountCode = registration.hotelDiscountCode || registration.hotel_discount_code || registration.discountCode || null;

    // Derive attendee name with robust fallbacks
    const fullName = registration.fullName || registration.name || [registration.firstName || registration.first_name, registration.lastName || registration.last_name].filter(Boolean).join(' ') || 'Valued Attendee';
    
    return (
      <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
        <dl className="sm:divide-y sm:divide-gray-200">
          {/* Name */}
          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">
              Name
            </dt>
            <dd className="mt-1 text-sm font-semibold text-gray-900 sm:mt-0 sm:col-span-2">
              {fullName}
            </dd>
          </div>
          
          {/* Status */}
          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">
              Registration Status
            </dt>
            <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
              <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                {registration.status || status || 'completed'}
              </span>
            </dd>
          </div>
          
          {/* Email */}
          {registration.email && (
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Email
              </dt>
              <dd className="mt-1 text-sm text-gray-600 sm:mt-0 sm:col-span-2">
                {registration.email}
              </dd>
            </div>
          )}
          
          
          
          {/* Hotel Discount Code Section */}
          {discountCode && (
            <div className="py-6 sm:py-6 sm:px-6 bg-yellow-50 rounded-lg mb-6">
              <h4 className="text-lg font-semibold text-yellow-900 mb-2">Your Hotel Discount Code:</h4>
              <div className="inline-block bg-white border border-yellow-400 rounded-md px-3 py-1">
                <code className="text-xl font-bold text-orange-500">{discountCode}</code>
              </div>
              <p className="text-sm text-gray-700 mt-2">Use this code when booking your hotel to receive a special discount.</p>
            </div>
          )}
          
          {/* Hotel Booking Section */}
          <div className="py-6 sm:py-6 sm:px-6 bg-blue-50">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Hotel Accommodation</h4>
            <p className="text-sm text-gray-600 mb-4">
              Book your stay at our partner hotels using the links below. Special rates are available for conference attendees.
            </p>
            <div className="grid gap-4 sm:grid-cols-2 mt-4">
              <a
                href="https://forms.gle/1Kw1rCrN8ixoF8DQA"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:border-blue-500 transition-colors"
              >
                <h5 className="font-medium text-gray-900">Best Western Plus Addis Ababa</h5>
                <p className="text-sm text-blue-600 mt-1">Book Now →</p>
              </a>
              <a
                href="https://bookings.hotelrez.co.uk/?adult=1&arrive=2025-10-10&chain=19159&child=0&currency=USD&depart=2025-10-11&hotel=9091&level=hotel&locale=en-US&nocache=true&productcurrency=USD&promo=CYF%402025&rooms=1"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:border-blue-500 transition-colors"
              >
                <h5 className="font-medium text-gray-900">Ethiopian Skylight Hotel</h5>
                <p className="text-sm text-blue-600 mt-1">Book Now →</p>
              </a>
              <a
                href="https://bookings.hotelrez.co.uk/?adult=1&arrive=2025-10-10&chain=19159&child=0&currency=USD&depart=2025-10-11&hotel=9091&level=hotel&locale=en-US&nocache=true&productcurrency=USD&promo=CYF%402025&rooms=1"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:border-blue-500 transition-colors"
              >
                <h5 className="font-medium text-gray-900">Marriott Executive Apt.</h5>
                <p className="text-sm text-blue-600 mt-1">Book Now →</p>
              </a>
            </div>
          </div>
          
          {/* Airline Booking Section */}
          <div className="py-6 sm:py-6 sm:px-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Flight Bookings</h4>
            <p className="text-sm text-gray-600 mb-4">
              Ethiopian Airlines is the official carrier for the Africa Maritime Conference. Special fares are available for attendees.
            </p>
            <a
              href="https://www.ethiopianairlines.com/events/africa-maritime-conference"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Book Your Flight with Ethiopian Airlines
            </a>
          </div>
          
          {/* Support Section */}
          <div className="py-6 sm:py-6 sm:px-6 bg-gray-50 border-t border-gray-200">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h4>
            <p className="text-sm text-gray-600">
              For any questions regarding your registration, hotel bookings, or flights, please contact our support team at{' '}
              <a href="mailto:support@africamaritimeconf.com" className="text-blue-600 hover:underline">
                mail@africamaritimeconf.com
              </a>
              .
            </p>
          </div>
        </dl>
      </div>
    );
  };

  return (
    <section className="" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>


    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gray-50 pt-32 pb-12 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-3xl font-bold leading-6 text-gray-900">
              Registration Successful! 🎉
            </h3>
            <p className="mt-2 max-w-2xl text-lg text-gray-700">
              Thank you for registering for the Africa Maritime Conference. Your payment has been processed successfully. 
            </p>
            <p className="mt-2 text-gray-500">
              A confirmation email has been sent to {registration?.email || 'your email address'} with your registration details.
            </p>
          </div>
          
          {renderRegistrationDetails()}
          
          <div className="px-4 py-5 sm:p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">What happens next?</h4>
            <ul className="space-y-4">
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 text-green-500 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="ml-3 text-gray-700">
                  We've sent a confirmation email to your registered email address.
                  {receiptDownloaded && (
                    <span className="text-green-600 ml-2">✔ Receipt PDF has been downloaded to your device.</span>
                  )}
                </p>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 text-green-500 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="ml-3 text-gray-700">
                  You'll receive additional information about the conference schedule and venue as the event approaches.
                </p>
              </li>
            </ul>
          </div>
          
          <div className="px-4 py-4 bg-gray-50 sm:px-6 flex justify-between items-center">
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Print Confirmation
            </button>
            <a
              href="/"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to Home
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  
    </section>
  )
}

export default TravelBookings
